import type { CallOutcome, Contact } from '@schwarz/contract';

/**
 * How often to re-read while a call is actually in flight.
 *
 * A resolution is imminent — the webhook arrives seconds after the call ends —
 * so this is the one case worth watching closely.
 */
export const DIALING_POLL_MS = 2500;

/**
 * How often to re-read while the queue holds something.
 *
 * Slower, because nothing is happening yet: we are waiting for the worker to
 * start a call, which it does on its own schedule. Ten seconds is well inside
 * the worker's fifteen, so a call that starts is visible before the next one
 * could.
 */
export const QUEUE_POLL_MS = 5000;

/** Outcomes that mean the call is over. */
const RESOLVED: readonly CallOutcome[] = ['ANSWERED', 'MISSED', 'FAILED'];

function isResolved(outcome: CallOutcome | null): boolean {
  return outcome !== null && RESOLVED.includes(outcome);
}

/**
 * How fast to poll the table, or undefined for not at all.
 *
 * ## The bug this replaces
 *
 * Polling used to start only once a `DIALING` row was already visible, which is
 * circular: a call started by the queue worker or the scheduler happens entirely
 * server-side, so nothing tells the browser to look, so no row ever turns
 * `DIALING`, so polling never begins. The operator saw "Calling…" only after
 * reloading the page by hand — which is exactly the case the live state exists
 * for.
 *
 * So the question is not "is a call in flight" but "could the list change
 * without the operator touching anything". Two things make that true: a call
 * already running, and a queue with something in it. If neither holds, nothing
 * can start without a user action, and a user action invalidates the cache
 * anyway.
 */
export function pollingInterval(
  rows: readonly Contact[],
  queueCount: number,
  callsInFlight = 0,
): number | undefined {
  // `callsInFlight` counts calls, not queue entries, which closes a gap the row
  // check alone left open: an instant call to a contact on another page of the
  // table is in progress and invisible here, so nothing polled and the row never
  // caught up. Asking the server how many calls are live does not depend on
  // which rows happen to be on screen.
  if (callsInFlight > 0) return DIALING_POLL_MS;
  if (rows.some((row) => row.lastOutcome === 'DIALING')) return DIALING_POLL_MS;
  if (queueCount > 0) return QUEUE_POLL_MS;
  return undefined;
}

/** What we remember about a row between reads. */
export interface Tracked {
  name: string;
  outcome: CallOutcome | null;
}

export type ActivitySnapshot = ReadonlyMap<string, Tracked>;

export function takeSnapshot(rows: readonly Contact[]): ActivitySnapshot {
  return new Map(rows.map((row) => [row.id, { name: row.name, outcome: row.lastOutcome }]));
}

export interface ActivityToast {
  kind: 'success' | 'error' | 'info';
  message: string;
}

/**
 * What to tell the operator about calls that finished since the last read.
 *
 * ## Why a vanished row still gets a message
 *
 * A contact with only an unresolved call sits in the waiting list; the moment
 * its call resolves it belongs to the history list and disappears from under
 * the operator's cursor. That is the behaviour the mock asks for, but silently
 * removing the row the operator was watching is not — so a row that was dialling
 * and is now absent produces a message too. We cannot name its outcome, because
 * the row carrying it is no longer in the response, and saying so is better than
 * guessing.
 *
 * ## What the caller must guarantee
 *
 * Both arguments have to describe the *same* query. Switching tab, page or
 * search changes which rows are visible for reasons that have nothing to do with
 * calls, and a row that vanished because the operator paged away has not
 * finished. The caller discards its snapshot when the query changes.
 */
export function resolutionToasts(
  previous: ActivitySnapshot,
  rows: readonly Contact[],
): ActivityToast[] {
  const current = new Map(rows.map((row) => [row.id, row]));
  const toasts: ActivityToast[] = [];

  for (const [id, before] of previous) {
    // Only a call we watched start can be a call we watched finish.
    if (before.outcome !== 'DIALING') continue;

    const now = current.get(id);

    if (!now) {
      toasts.push({
        kind: 'info',
        message: `Call to ${before.name} finished — moved to Call history`,
      });
      continue;
    }

    if (!isResolved(now.lastOutcome)) continue;

    toasts.push(describe(now));
  }

  return toasts;
}

/** One finished call, in the operator's terms rather than the schema's. */
function describe(row: Contact): ActivityToast {
  const name = row.name;
  switch (row.lastOutcome) {
    case 'ANSWERED':
      return { kind: 'success', message: `${name} answered` };
    case 'MISSED':
      // Neither good nor bad news, and by far the most common outcome. See the
      // note on why the toast store has a neutral kind.
      return row.lastDisposition === 'BUSY'
        ? { kind: 'info', message: `${name} was busy or declined the call` }
        : { kind: 'info', message: `${name} did not answer` };
    case 'FAILED':
      return row.lastDisposition === 'PLATFORM_TERMINATED'
        ? { kind: 'error', message: `Call to ${name} was stopped by the platform` }
        : { kind: 'error', message: `Call to ${name} could not be completed` };
    default:
      return { kind: 'info', message: `Call to ${name} finished` };
  }
}
